/**
 * @module DragAndDropGeoJSON
 *
 * GeoJSON format used by the DragAndDrop interaction. RFC 7946 removed the
 * `crs` member from the GeoJSON specification, so files found in the wild
 * declare their coordinate system in many different (often non-standard)
 * ways — or not at all. This format is deliberately benevolent about what it
 * accepts, and falls back to coordinate heuristics when nothing is declared.
 */
import GeoJSON from 'ol/format/GeoJSON';
import olProjection from 'ol/proj/Projection';
import {get as getProjection} from 'ol/proj';

/**
 * Provides the projection of the current map view. Evaluated lazily, so the
 * format keeps working when the map is re-initialized with a different view.
 */
export type ViewProjectionProvider = () => olProjection | undefined;

/**
 * Well-known non-EPSG coordinate system names mapped to their EPSG equivalent.
 */
const CRS_ALIASES: { [key: string]: string } = {
    'CRS84': 'EPSG:4326',
    'CRS:84': 'EPSG:4326',
    'CRS83': 'EPSG:4269',
    'CRS27': 'EPSG:4267',
    'WGS84': 'EPSG:4326',
    'WGS 84': 'EPSG:4326'
};

/**
 * Parses a single CRS identifier value into a normalized 'EPSG:xxxx' code.
 * Accepted notations (case-insensitive):
 * - number: `3067`
 * - numeric string: `"3067"`
 * - EPSG prefix: `"EPSG:3067"`, `"epsg::3067"`, `"EPSG 3067"`
 * - OGC URN: `"urn:ogc:def:crs:EPSG::3067"`, `"urn:x-ogc:def:crs:EPSG:8.9:3067"`
 * - OGC URI: `"http://www.opengis.net/def/crs/EPSG/0/3067"`
 * - legacy GML: `"http://www.opengis.net/gml/srs/epsg.xml#3067"`
 * - well-known names: `"CRS84"`, `"urn:ogc:def:crs:OGC:1.3:CRS84"`, `"WGS84"`
 * @param value - CRS identifier in any of the supported notations
 * @returns normalized EPSG code, e.g. 'EPSG:3067', or undefined when not recognized
 */
export function parseEpsgValue(value: unknown): string | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return 'EPSG:' + Math.trunc(value);
    }

    if (typeof value !== 'string') {
        return undefined;
    }

    const name = value.trim().toUpperCase();
    if (!name) {
        return undefined;
    }

    // Well-known names, either bare ("CRS84") or at the end of an OGC URN
    // ("urn:ogc:def:crs:OGC:1.3:CRS84").
    const alias = name.match(/(?:^|:)(CRS:?84|CRS83|CRS27|WGS\s?84)$/);
    if (alias) {
        return CRS_ALIASES[alias[1].replace(/\s+/, ' ')];
    }

    // "EPSG:3067", "EPSG::3067", "EPSG 3067" or a plain numeric code "3067".
    let match = name.match(/^(?:EPSG\s*:{1,2}\s*|EPSG\s+)?(\d{4,6})$/);
    if (match) {
        return 'EPSG:' + match[1];
    }

    // OGC URN: "urn:ogc:def:crs:EPSG::3067" (with optional version and x- prefix).
    match = name.match(/^URN:(?:X-)?OGC:DEF:CRS:EPSG:[^:]*:(\d+)$/);
    if (match) {
        return 'EPSG:' + match[1];
    }

    // OGC http URI: "http://www.opengis.net/def/crs/EPSG/0/3067".
    match = name.match(/OPENGIS\.NET\/DEF\/CRS\/EPSG\/[^/]*\/(\d+)/);
    if (match) {
        return 'EPSG:' + match[1];
    }

    // Legacy GML style: "http://www.opengis.net/gml/srs/epsg.xml#3067".
    match = name.match(/EPSG(?:\.XML)?#(\d+)$/);
    if (match) {
        return 'EPSG:' + match[1];
    }

    return undefined;
}

/**
 * Detects the EPSG code declared in a GeoJSON object. Understands the legacy
 * 2008 specification `crs` member in all of its shapes (`name`, `EPSG` and
 * `link` types) as well as common non-standard shortcuts (`"crs": "EPSG:3067"`,
 * `"crs": 3067`).
 * @param geojson - parsed GeoJSON object
 * @returns normalized EPSG code, e.g. 'EPSG:3067', or undefined when nothing is declared
 */
export function detectGeoJsonEpsg(geojson: unknown): string | undefined {
    if (!geojson || typeof geojson !== 'object') {
        return undefined;
    }

    const crs = (geojson as { [key: string]: unknown })['crs'];
    if (crs === undefined || crs === null) {
        return undefined;
    }

    // Non-standard shortcuts: "crs": "EPSG:3067" or "crs": 3067.
    if (typeof crs === 'string' || typeof crs === 'number') {
        return parseEpsgValue(crs);
    }

    if (typeof crs !== 'object') {
        return undefined;
    }

    const crsObject = crs as { [key: string]: unknown };
    const type = typeof crsObject['type'] === 'string' ? (crsObject['type'] as string).toLowerCase() : undefined;
    const properties = (crsObject['properties'] && typeof crsObject['properties'] === 'object'
        ? crsObject['properties']
        : {}) as { [key: string]: unknown };

    // Legacy 2003 specification: {"type": "EPSG", "properties": {"code": 3067}}.
    if (type === 'epsg') {
        return parseEpsgValue(properties['code']);
    }

    // {"type": "link", "properties": {"href": "http://.../epsg.xml#3067"}}.
    if (type === 'link') {
        return parseEpsgValue(properties['href']);
    }

    // {"type": "name", "properties": {"name": "urn:ogc:def:crs:EPSG::3067"}}
    // — and benevolent fallbacks for malformed variations of it.
    return parseEpsgValue(properties['name'])
        ?? parseEpsgValue(properties['code'])
        ?? parseEpsgValue(crsObject['name'])
        ?? parseEpsgValue(crsObject['code']);
}

/**
 * Finds the first coordinate in a GeoJSON object (FeatureCollection, Feature,
 * Geometry or GeometryCollection). Used for coordinate-magnitude heuristics.
 * @param geojson - parsed GeoJSON object
 * @returns first [x, y] coordinate, or undefined when the object contains none
 */
export function findFirstCoordinate(geojson: unknown): number[] | undefined {
    if (!geojson || typeof geojson !== 'object') {
        return undefined;
    }

    const object = geojson as { [key: string]: unknown };

    for (const collection of ['features', 'geometries']) {
        if (Array.isArray(object[collection])) {
            for (const entry of object[collection] as unknown[]) {
                const coordinate = findFirstCoordinate(entry);
                if (coordinate) {
                    return coordinate;
                }
            }
            return undefined;
        }
    }

    if (object['geometry']) {
        return findFirstCoordinate(object['geometry']);
    }

    let coordinates = object['coordinates'];
    while (Array.isArray(coordinates) && Array.isArray(coordinates[0])) {
        coordinates = coordinates[0];
    }
    if (Array.isArray(coordinates) && typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
        return coordinates as number[];
    }

    return undefined;
}

/**
 * GeoJSON format with benevolent coordinate system detection, used by the
 * DragAndDrop interaction.
 *
 * Resolution order for the data projection:
 * 1. Any recognized `crs` declaration (see {@link detectGeoJsonEpsg}). The
 *    declared system must be registered — either built-in (EPSG:4326/3857) or
 *    listed in the projection configuration (`coordinateSystem`).
 * 2. No declaration, coordinates within longitude/latitude bounds — EPSG:4326,
 *    the RFC 7946 default.
 * 3. No declaration, coordinates exceeding longitude/latitude bounds — the
 *    current view projection is assumed and a warning is logged.
 */
class DragAndDropGeoJSON extends GeoJSON {
    private readonly viewProjection: ViewProjectionProvider;

    /**
     * @param viewProjection - provider of the current view projection, used as
     * fallback for undeclared projected coordinates
     */
    constructor(viewProjection: ViewProjectionProvider) {
        super();
        this.viewProjection = viewProjection;
    }

    protected override readProjectionFromObject(object: any): olProjection {
        const declared = detectGeoJsonEpsg(object);

        if (declared) {
            const projection = getProjection(declared);
            if (projection) {
                return projection;
            }
            console.warn('[DragAndDrop] The dropped GeoJSON declares ' + declared + ', which is not registered. ' +
                'Add it to the projection configuration (coordinateSystem) to enable reprojection.');
            throw new Error('Unsupported coordinate system ' + declared + ' in dropped GeoJSON.');
        }

        if (object && typeof object === 'object' && object['crs'] !== undefined && object['crs'] !== null) {
            console.warn('[DragAndDrop] Could not understand the "crs" member of the dropped GeoJSON. ' +
                'Falling back to coordinate heuristics.');
        }

        const coordinate = findFirstCoordinate(object);
        if (coordinate && (Math.abs(coordinate[0]) > 180 || Math.abs(coordinate[1]) > 90)) {
            const viewProjection = this.viewProjection();
            if (viewProjection) {
                console.warn('[DragAndDrop] The dropped GeoJSON declares no coordinate system and its coordinates ' +
                    'exceed longitude/latitude bounds. Assuming the map projection ' + viewProjection.getCode() + '.');
                return viewProjection;
            }
        }

        // RFC 7946: GeoJSON coordinates are in WGS 84.
        return getProjection('EPSG:4326') as olProjection;
    }
}

export default DragAndDropGeoJSON;
